import { createFileRoute } from '@tanstack/react-router';
import { AdminSubjectDraftProvider, SubjectDraft, LabelDraft, defaultDraft } from '@/contexts';
import { AddSubjectWizard, AccessDenied, NotFound, ServerError } from '@/components';
import { useSubjectConfig, useAreaConfig } from '@/hooks';
import { SubjectConfig } from '#server/models';

/** Strip CloudFront/S3 signed URL params, returning just the storage path. */
const extractPath = (url: string): string => {
  try {
    return new URL(url).pathname.replace(/^\//, '');
  } catch {
    return url;
  }
};

const generateId = () => Math.random().toString(36).slice(2);

const subjectConfigToDraft = (config: SubjectConfig): SubjectDraft => {
  const c3d = config.config3D;

  const config3D: SubjectDraft['config3D'] = c3d
    ? {
        modelPath: extractPath(c3d.modelPath),
        texturePath: extractPath(c3d.texturePath),
        contextModelPath: extractPath(c3d.contextModelPath),
        referenceContextModelPath: extractPath(c3d.referenceContextModelPath),
        // Use the signed URLs directly as preview URLs (valid for 24h from CloudFront)
        _modelBlobUrl: c3d.modelPath,
        _textureBlobUrl: c3d.texturePath,
        _contextBlobUrl: c3d.contextModelPath,
        _referenceContextBlobUrl: c3d.referenceContextModelPath,
        scale: c3d.scale,
        position: c3d.position,
        rotation: c3d.rotation,
        distMin: c3d.distMin,
        distMax: c3d.distMax,
        dist: c3d.dist,
        ambientLight: c3d.ambientLight,
        axes: c3d.axes,
        axesLabels: c3d.axesLabels,
        labelVisibleDist: c3d.labelVisibleDist,
        labelVisibleRange: c3d.labelVisibleRange,
        context: {
          subjectContextEnabled: !!c3d.context.subjectContext,
          subjectContext: c3d.context.subjectContext ?? defaultDraft.config3D.context.subjectContext,
          referenceContext: c3d.context.referenceContext,
          fullContext: c3d.context.fullContext
        }
      }
    : defaultDraft.config3D;

  const labels: LabelDraft[] = config.labels.map((l) => ({
    _id: generateId(),
    id: l.id,
    title: l.title,
    slug: l.slug,
    description: l.description,
    primaryCategoryId: l.primaryCategoryId ?? null,
    categoryIds: l.categoryIds,
    unitIds: l.unitIds,
    config3D: l.config3D ?? defaultDraft.labels[0]?.config3D ?? {
      position: [0, 0, 0],
      camera: {
        rotation: { azimuthAngle: 0, polarAngle: Math.PI / 2 },
        position: [0, 0, 100],
        focalOffset: [0, 0, 0],
        distance: 100
      }
    }
  }));

  return {
    id: config.id,
    title: config.title,
    slug: config.slug,
    type: config.type,
    disciplineId: config.discipline,
    description: config.description,
    thumbnailPath: config.thumbnailPath,
    unitIds: config.unitIds,
    categoryIds: config.categoryIds,
    config3D,
    labels,
    currentStep: 1,
    highestStepReached: 7
  };
};

const SubjectEditPage = () => {
  const { areaSlug, subjectSlug } = Route.useParams();
  const { error: areaError } = useAreaConfig(areaSlug, 'EDITOR');
  const { data: subjectConfig, isLoading, error: subjectError } = useSubjectConfig(subjectSlug, '3D', areaSlug);

  // Check editor access first — produces the editor-specific error message rather
  // than the viewer-level FORBIDDEN that useSubjectConfig would surface.
  if (areaError) {
    switch (areaError.data?.code) {
      case 'FORBIDDEN':
        return <AccessDenied error={areaError.data.accessError as any} />;
      case 'NOT_FOUND':
        return <NotFound message={`Area "${areaSlug}" could not be found.`} />;
      default:
        return <ServerError />;
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-[#999]">
        Loading subject "{subjectSlug}"…
      </div>
    );
  }

  if (subjectError) {
    switch (subjectError.data?.code) {
      case 'FORBIDDEN':
        return <AccessDenied error={subjectError.data.accessError as any} />;
      case 'NOT_FOUND':
        return <NotFound message={`Subject "${subjectSlug}" could not be found in area "${areaSlug}".`} />;
      default:
        return <ServerError />;
    }
  }

  if (!subjectConfig) {
    return <ServerError />;
  }

  return (
    <AdminSubjectDraftProvider
      areaSlug={areaSlug}
      draftKey={`${areaSlug}-${subjectSlug}`}
      isNew={false}
      initialDraft={subjectConfigToDraft(subjectConfig)}
    >
      <AddSubjectWizard />
    </AdminSubjectDraftProvider>
  );
};

export const Route = createFileRoute('/_adminLayout/admin/areas/$areaSlug/subjects/$subjectSlug')({
  component: SubjectEditPage
});
